import { useEffect, useRef, useState } from 'react';

import { Button, Divider, FormControl, FormErrorMessage, FormLabel, Input, Tooltip, useDisclosure, useToast } from '@chakra-ui/react';
import { Formik, Field, Form } from 'formik';
import { HttpStatusCode } from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';

import ArrowIcon from '@/assets/ArrowIcon';
import { COLORS, DURATION_TOAST } from '@/common/constants';
import ModalConfirmation from '@/components/Modals/ModalConfirmation';
import TablePrivilege from '@/components/Tables/TablePrivilege';
import SelectRole from '@/components/Selects/SelectRole';
import { useAppSelector } from '@/redux/hooks';
import { RootState } from '@/redux/store';
import { addPrivilegeSchema } from '@/schemas/addPrivilegeSchema';
import { getRoleData } from '@/services/roleService';
import { getDataPrivilegeByUser, postAssignPrivilegeToUser } from '@/services/privilegeService';
import { getAssignedPrivileges, getRevokedPrivileges, listInitialPrivileges } from '@/utils/privileges';

const UserPrivilege = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const navigate = useNavigate();
    const tablePrivilegeRef = useRef() as any;
    const location = useLocation();
    const name = useAppSelector((state: RootState) => state.account.name);

    const { userId, roleId: currentRoleId, firstName, lastName, companyName } = location.state || {};

    const [listRole, setListRole] = useState([]) as any;
    const [listPrivilege, setListPrivilege] = useState([]) as any;
    const [roleId, setRoleId] = useState('');
    const [dataEdit, setDataEdit] = useState(null) as any;
    const [title, setTitle] = useState("");
    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [initiallyAllowed, setInitiallyAllowed] = useState<string[]>([]);
    const [initiallDenied, setInitiallyDenied] = useState<string[]>([]);

    const handleChangeRole = (value: string, props: any) => {
        props.setFieldValue('role', value);
        setRoleId(value);
    }

    const handleCancel = () => {
        onOpen();
        setTitle("Cancel to Update Privilege?");
        setConfirmationTest("Are you sure you want to cancel?");
        setConfirmationTitleButton("Yes");
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    const handleSubmit = () => {
        setTitle("Update This Privilege?");
        setConfirmationTest("Are you sure you want to update?");
        setConfirmationTitleButton("Update Privilege");
        onOpen();
        setIsSubmitting(true);
        if (tablePrivilegeRef.current) {
            const checkedPrivileges = tablePrivilegeRef.current.getCheckedPrivileges();
            const uncheckedPrivileges = tablePrivilegeRef.current.getUncheckedPrivileges();
            const assignedPrivileges = getAssignedPrivileges(checkedPrivileges, initiallDenied);
            const revokedPrivileges = getRevokedPrivileges(uncheckedPrivileges, initiallyAllowed);
            const obj = {
                assignPrivileges: assignedPrivileges,
                revokePrivileges: revokedPrivileges,
                userId,
                createdBy: name
            };
            setDataEdit(obj);
        }
    }

    const handleConfirm = async () => {
        onClose();

        if (title === "Cancel to Update Privilege?") {
            navigate('/user-management/users');
            return;
        }

        const response = await postAssignPrivilegeToUser(dataEdit) as any;
        if (response.status === HttpStatusCode.Ok) {
            toast({
                title: 'User updated.',
                description: "We've updated this privilege.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(() => {
                navigate('/user-management/users');
            }, 1000)
        } else {
            toast({
                title: 'Privilege failed to update.',
                description: "We unabled to update this privilege.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const getListRole = async () => {
        const response = await getRoleData() as any;
        if (response.status === HttpStatusCode.Ok) {
            const formattedOptions = response.data.map((item: any) => ({ value: item.roleId, label: item.roleName }));
            setListRole(formattedOptions);
        }
    }

    const getPrivilege = async () => {
        const response = await getDataPrivilegeByUser(userId) as any;
        if (response.status === HttpStatusCode.Ok) {
            setListPrivilege(response.data);
            const privilegeList = listInitialPrivileges(response.data);
            setInitiallyAllowed(privilegeList.allowed);
            setInitiallyDenied(privilegeList.denied);
        }
    }

    useEffect(() => {
        getListRole();
    }, []);

    useEffect(() => {
        getPrivilege();
    }, [userId]);

    useEffect(() => {
        setRoleId(currentRoleId);
    }, [currentRoleId]);

    return (
        <>
            <ModalConfirmation isOpen={isOpen} onClose={onClose} title={title} handleCancel={handleConfirmToCancel} handleConfirm={handleConfirm} confirmationText={confirmationText} confirmButtonText={confirmationTitleButton} />
            <div className="form-container">
                <Formik
                    initialValues={{
                        role: roleId,
                        firstName,
                        lastName,
                        companyName
                    }}
                    validationSchema={addPrivilegeSchema}
                    onSubmit={() => {
                        handleSubmit();
                    }}
                    enableReinitialize={true}
                >
                    {(props) => (
                        <Form>
                            <Field name="role">
                                {({ form }: any) => (
                                    <FormControl
                                        isInvalid={form.errors.role && form.touched.role}
                                        style={{ marginBottom: 15 }}
                                    >
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">
                                                Role
                                            </FormLabel>
                                            <div className="form-input-container">
                                                <SelectRole
                                                    name="role"
                                                    options={listRole}
                                                    handleChange={({ value }: any) => handleChangeRole(value, props)}
                                                    value={props.values.role}
                                                    defaultValue={props.values.role}
                                                    hasError={form.errors.role && form.touched.role}
                                                    style={{ width: "100%", marginRight: "8px" }}
                                                    isDisabled
                                                />
                                                <FormErrorMessage>
                                                    {form.errors.role}
                                                </FormErrorMessage>
                                            </div>
                                        </div>
                                        
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='firstName'>
                                {({ field }: any) => (
                                    <FormControl style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">First Name</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='First Name' className="form-input" disabled />
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='lastName'>
                                {({ field }: any) => (
                                    <FormControl style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Last Name</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Last Name' className="form-input" disabled />
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Field name='companyName'>
                                {({ field }: any) => (
                                    <FormControl style={{ marginBottom: 15 }}>
                                        <div className="form-field-container">
                                            <FormLabel className="form-label">Company Name</FormLabel>
                                            <div className="form-input-container">
                                                <Input {...field} placeholder='Company Name' className="form-input" disabled />
                                            </div>
                                        </div>
                                    </FormControl>
                                )}
                            </Field>
                            <Divider mb={5} />
                            <TablePrivilege
                                ref={tablePrivilegeRef}
                                initialDataList={listPrivilege}
                                roleId={roleId}
                                isLoading={false}
                            />
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    mt={4}
                                    bgColor='white'
                                    borderColor='#D0D5DD'
                                    isLoading={isSubmitting}
                                    marginRight={5}
                                    onClick={handleCancel}
                                >
                                    Cancel
                                </Button>
                                <Tooltip content="This is the tooltip content">
                                    <Button
                                        mt={4}
                                        bgColor={COLORS.BUTTON.BACKGROUND}
                                        color={COLORS.BUTTON.FOREGROUND}
                                        marginRight={5}
                                        isLoading={isSubmitting}
                                        type='submit'
                                        rightIcon={<ArrowIcon />}
                                    >
                                        Save
                                    </Button>
                                </Tooltip>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </>
    )
}

export default UserPrivilege;
